import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppInformationService, AuthService, EntitiesService, GenesisUnitOfWork, ReportService, SessionInformationService } from 'app/core/core.module';
import { AssemblyJig } from 'app/core/model/assembly-jig';
import { AssemblyJigNote } from 'app/core/model/assembly-jig-note';
import { Dealer } from 'app/core/model/dealer';
import { Order } from 'app/core/model/order';
import { OrderLineDetail } from 'app/core/model/order-line-detail';
import { OrderLineDetailNote } from 'app/core/model/order-line-detail-note';
import { PartType } from 'app/core/model/part-type';
import { Setting } from 'app/core/model/setting';
import { ShipAddress } from 'app/core/model/ship-address';
import { OrderBuildDetailComponent } from 'app/orders/order-build-detail/order-build-detail.component';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { confirm, custom } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { OptionsDialogComponent } from '../options-dialog/options-dialog.component';

@Component({
    selector: 'app-build-scheduler',
    templateUrl: './build-scheduler.component.html',
    styleUrls: ['./build-scheduler.component.scss'],
    standalone: false,
})
export class BuildSchedulerComponent implements OnInit {
    reportService = inject(ReportService);
    route = inject(ActivatedRoute);
    sessionInfo = inject(SessionInformationService);

    scheduleDate: Date;
    today: Date;
    dealers: Dealer[];
    filter: any = {
        dealerId: null,
        salesOrderNumber: null,
        partNumber: null,
        vIN: null,
        startDate: null,
        endDate: null,
        assemblyJigIds: [],
    };
    allAssemblyJigs: AssemblyJig[];
    assemblyJigs: any[];
    interval: any;

    jobs: any;
    uncompletedJobs: any;
    assignedJobs: any;
    unAssignedJobs: any;
    stagedJobs: any;

    jigColumns: any[];
    jigContextMenuItems = [];
    jobContextMenuItems = [];
    unassignedJobContextMenuItems = [];

    contextMenuJobId: number;
    contextMenuJigId: number;
    teamMembers: any;
    filterOps: any[] = ['Contains', 'Does Not Contain'];
    shipAddresses: ShipAddress[];
    multiLocationDealerIds = new Set<number>();
    dailyProductionCapacity: number;
    projectedJobs: any;
    isDayLocked = false;
    dayLockNotes: any[] = [];
    showLockHistory = false;
    viewMode: 'day' | 'week' = 'day';
    weekDays: Date[] = [];
    weekDayColumns: any[] = [];

    get displayDate() {
        if (this.viewMode === 'week') {
            const start = moment(this.scheduleDate).startOf('isoWeek');
            const end = start.clone().add(4, 'days');
            return `Week of ${start.format('M/D')} – ${end.format('M/D/YYYY')}`;
        }
        return moment(this.scheduleDate).format('dddd, MMMM D, YYYY');
    }

    get jigOrderKey() {
        return `build-scheduler-jig-order-${this.auth.currentUser.id}`;
    }

    constructor(
        private uow: GenesisUnitOfWork,
        private auth: AuthService,
        private appInfo: AppInformationService,
        private modalService: BsModalService,
        private entitiesService: EntitiesService,
    ) {}

    async ngOnInit() {
        console.log(this.filterOps);
        this.dealers = (await this.uow.getDealers(true)) as Dealer[];
        this.today = moment(new Date()).startOf('day').toDate();
        this.teamMembers = await this.uow.getJigTeamMembers();
        this.allAssemblyJigs = (await this.uow.assemblyJigs.whereActive('displayOrder')) as AssemblyJig[];
        this.shipAddresses = (await this.uow.shipAddresses.all()) as ShipAddress[];
        this.dailyProductionCapacity = ((await this.uow.getSettingsByName('DailyProductionCapacity')) as Setting)?.intValue;
        this.multiLocationDealerIds = new Set(
            _(this.shipAddresses)
                .countBy('dealerId')
                .pickBy((count) => count > 1)
                .keys()
                .map(Number)
                .valueOf(),
        );
        this.route.params.subscribe((params) => {
            this.scheduleDate = new Date(params['scheduled']);
        });

        if (this.scheduleDate && !isNaN(this.scheduleDate.getTime())) {
            this.filter.dueDate = this.scheduleDate;
        } else this.scheduleDate = this.today;

        this.jigContextMenuItems = [
            { index: 1, text: 'Rename' },
            { index: 2, text: 'Edit Team Members' },
            { index: 3, text: 'Add Jig Note' },
            { index: 4, text: 'ReSchedule' },
            { index: 5, text: 'Print' },
        ];
        this.unassignedJobContextMenuItems = [
            { index: 1, text: 'Open' },
            { index: 2, text: 'Add PreAssembly Note' },
            { index: 7, text: 'Put On Hold' },
            { index: 8, text: 'Remove Hold' },
        ];
        this.jobContextMenuItems = [
            { index: 1, text: 'Open' },
            { index: 2, text: 'Add Build Note' },
            { index: 3, text: 'UnAssign' },
            { index: 4, text: 'ReSchedule' },
            { index: 5, text: 'Complete' },
            { index: 6, text: 'UnComplete' },
        ];

        const filter = this.sessionInfo.getItem('build-scheduler-jig-filter', true);
        if (filter) {
            this.filter.assemblyJigIds = JSON.parse(filter).assemblyJigIds;
        }
        await this.getAssemblyJigs();
        await this.refresh();
    }

    async ngOnDestroy() {
        clearInterval(this.interval);
    }

    search() {
        this.refresh();
    }
    clear() {
        const filter = this.sessionInfo.getItem('build-scheduler-jig-filter', true);
        this.filter = {
            dealerId: null,
            salesOrderNumber: null,
            partNumber: null,
            vIN: null,
            startDate: null,
            endDate: null,
            assemblyJigIds: filter ? JSON.parse(filter).assemblyJigIds : [],
        };
        this.refresh();
    }

    async refresh() {
        this.appInfo.pleaseWaitShow();
        await this.getAssemblyJigs();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }
    async applyJigFilter() {
        this.sessionInfo.setItem('build-scheduler-jig-filter', JSON.stringify(this.filter), true);
        this.appInfo.pleaseWaitShow();
        await this.getAssemblyJigs();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async getAssemblyJigs() {
        this.assemblyJigs = this.allAssemblyJigs.filter((jig) => this.filter.assemblyJigIds.includes(jig.id));

        const teamMembers = await this.uow.getAssemblyJigTeamMembersByDate(this.scheduleDate);
        const jigNotes = await this.uow.getAssemblyJigNotesByDate(this.scheduleDate);

        this.jigColumns = [];
        this.assemblyJigs.forEach((jig) => {
            this.jigColumns.push({
                id: jig.id,
                displayOrder: jig.displayOrder,
                name: jig.name,
                assignedJobs: [],
                teamMembers: teamMembers.filter((member) => member.assemblyJigId === jig.id),
                jigNotes: jigNotes.filter((note) => note.assemblyJigId === jig.id),
            });
        });

        // apply aved column order; jigs not in it keep their default order at the end
        const savedOrder: number[] = this.sessionInfo.getItem(this.jigOrderKey, true) || [];
        const position = (id: number) => (savedOrder.includes(id) ? savedOrder.indexOf(id) : savedOrder.length);
        this.jigColumns = _.sortBy(this.jigColumns, [(column) => position(column.id), 'displayOrder']);
    }

    async getData() {
        const jobsExpand = 'orderLine.order.dealer, status, orderLine.part.partPartTypes.partType, notes.createdBy';
        const [jobs, assignedJobs, otherJigJobs, uncompletedJobs] = await Promise.all([
            this.uow.getOrderLineDetailsForBuildScheduler(jobsExpand, this.filter),
            this.uow.getOrderLineDetailsByDate(this.scheduleDate, 'orderLine.order.dealer, status, orderLine.part, notes.createdBy, optionDetails.orderLine.part', true),
            //get the jobs assigned to the Other jig (id=6)
            this.uow.getOrderLineDetailsByAssemblyJigId(6),
            this.uow.getOrderLineDetailsUncompletedOnPreviousDays(this.scheduleDate, jobsExpand),
        ]);
        jobs.forEach((job: any) => {
            job.warranty = job.orderLine.order.typeId === Order.Types.Warranty;
        });
        this.jobs = jobs;
        this.uncompletedJobs = uncompletedJobs;

        //merge the two arrays, avoiding duplicates
        otherJigJobs.forEach((job) => {
            const existingJob = assignedJobs.find((j) => j.id === job.id);
            if (!existingJob) {
                assignedJobs.push(job);
            }
        });

        console.log('Uncompleted Jobs:', this.scheduleDate, uncompletedJobs);

        assignedJobs.forEach((job: any) => {
            job.shipToCityDisplay = this.getShipToCityDisplay(job);
            job.assemblyNotes = job.notes.filter((note) => note.assemblyNote && !note.deleted);
            job.buildTime = job.orderLine?.part?.buildTime + job.optionDetails.reduce((acc, child) => acc + child.orderLine?.part?.assemblyTime, 0);
            job.preAssemblyNotes = _.orderBy(
                job.notes.filter((note) => note.preAssemblyNote && !note.deleted),
                ['createDate'],
                ['desc'],
            );
        });

        await this.entitiesService.setShipAddressNotes(assignedJobs, this.shipAddresses);

        this.assignedJobs = assignedJobs.filter((job) => {
            return job.assemblyJigId;
        });

        // projection ignores the search filter, so it needs the full list when one is set
        const searchActive = ['dealerId', 'dealerName', 'salesOrderNumber', 'partNumber', 'startDate', 'endDate'].some((key) => !!this.filter[key]);
        const allJobs = searchActive ? await this.uow.getOrderLineDetailsForBuildScheduler(jobsExpand, {}) : this.jobs;
        const projectedJobs = await this.computeProjection(
            _.orderBy(
                allJobs.filter((job) => this.isUnassignedCandidate(job) && !job.onHold),
                ['buildDue', (job) => job.orderLine?.order?.invoiceNumber, 'id'],
            ),
        );

        const unAssignedJobs = _.orderBy(
            this.jobs.filter((job) => this.isUnassignedCandidate(job) && (!job.onHold || this.filter.includeOnHold)),
            ['buildDue', (job) => job.orderLine?.order?.invoiceNumber, 'id'],
        );
        [...unAssignedJobs, ...projectedJobs].forEach((job) => {
            job.shipToCityDisplay = this.getShipToCityDisplay(job);
            job.preAssemblyNotes = _.orderBy(
                job.notes.filter((note) => note.preAssemblyNote && !note.deleted),
                ['createDate'],
                ['desc'],
            );
        });

        await this.entitiesService.setShipAddressNotes([...unAssignedJobs, ...projectedJobs], this.shipAddresses);

        this.unAssignedJobs = {
            id: -1,
            name: 'unAssignedJobs',
            assignedJobs: unAssignedJobs,
        };
        this.projectedJobs = {
            id: -2,
            name: 'projectedJobs',
            assignedJobs: projectedJobs,
        };

        this.jigColumns.forEach((jig: any) => {
            jig.assignedJobs = _(this.assignedJobs)
                .filter((x) => x.assemblyJigId === jig.id)
                .orderBy((x) => x.scheduleDisplayOrder)
                .valueOf();
        });
        await this.getJigTimes();
        if (this.viewMode === 'week') await this.getWeekData();
    }

    // only dealers with more than one ship-to location get the city after their name
    getShipToCityDisplay(job: any) {
        const order = job.orderLine?.order;
        if (!order || !this.multiLocationDealerIds.has(order.dealerId)) return null;
        return order.shipToCity?.trim();
    }

    isUnassignedCandidate(job: any) {
        return (
            !job.assemblyJigId &&
            !job.buildComplete &&
            job.statusId !== OrderLineDetail.Statuses.Complete &&
            job.orderLine.part.partPartTypes.some((partType) => partType.partTypeId === PartType.Types.Trailer)
        );
    }

    // fill each weekday from today through the schedule date up to capacity, carrying the queue forward
    async computeProjection(queue: any[]) {
        const scheduleDay = moment(this.scheduleDate).startOf('day');
        const isPast = scheduleDay.isBefore(this.today);
        const [locks, counts] = await Promise.all([
            this.uow.getAssemblyJigLocks(isPast ? this.scheduleDate : this.today, this.scheduleDate),
            this.uow.getScheduledCountsByDay(this.today, this.scheduleDate),
        ]);
        const lockedDays = new Set(locks.filter((lock) => lock.isLocked).map((lock) => moment(lock.date).format('YYYY-MM-DD')));
        this.isDayLocked = lockedDays.has(scheduleDay.format('YYYY-MM-DD'));
        const dayLock = locks.find((lock) => moment(lock.date).isSame(scheduleDay, 'day'));
        this.dayLockNotes = dayLock ? await this.uow.getNotes('AssemblyJigLock', dayLock.id) : [];
        if (isPast || this.isDayLocked || !this.dailyProductionCapacity) return [];

        let next = 0;
        for (const day = moment(this.today); !day.isAfter(scheduleDay); day.add(1, 'days')) {
            if (day.isoWeekday() > 5 || lockedDays.has(day.format('YYYY-MM-DD'))) continue;
            const slots = Math.max(0, this.dailyProductionCapacity - (counts[day.format('YYYY-MM-DD')] || 0));
            if (day.isSame(scheduleDay, 'day')) return queue.slice(next, next + slots);
            next += slots;
        }
        return [];
    }

    async toggleDayLock() {
        const action = this.isDayLocked ? 'Unlock' : 'Lock';
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: `${action} ${this.displayDate}`,
                dialogPrompt: 'Note',
                mode: 'textarea',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
            if (!result.result) return;

            this.appInfo.pleaseWaitShow();
            let lock = (await this.uow.getAssemblyJigLocks(this.scheduleDate, this.scheduleDate))[0];
            if (lock) lock.isLocked = !this.isDayLocked;
            else lock = this.uow.assemblyJigLocks.createEntity({ date: moment(this.scheduleDate).startOf('day').toDate(), isLocked: true });
            await this.uow.commit();
            this.uow.notes.createEntity({
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
                notes: result.value?.trim() ? `${action}ed: ${result.value.trim()}` : `${action}ed`,
                entityTypeName: 'AssemblyJigLock',
                primaryKey: lock.id,
            });
            await this.uow.commit();
            await this.getData();
            this.appInfo.pleaseWaitHide();
        });
    }

    async getJigTimes() {
        const jigTimes = await this.uow.getAssemblyJigTimes(this.scheduleDate);
        this.jigColumns.forEach((jig: any) => {
            const jigTime = jigTimes.find((time) => time.assemblyJigId === jig.id);
            jig.estimatedTime = jigTime ? jigTime.buildTime : 0;
        });
    }

    async toggleViewMode() {
        this.viewMode = this.viewMode === 'day' ? 'week' : 'day';
        if (this.viewMode === 'week') {
            this.appInfo.pleaseWaitShow();
            await this.getWeekData();
            this.appInfo.pleaseWaitHide();
        }
    }

    computeWeekDays() {
        const monday = moment(this.scheduleDate).startOf('isoWeek');
        this.weekDays = Array.from({ length: 5 }, (_, i) => monday.clone().add(i, 'days').toDate());
    }

    async getWeekData() {
        this.computeWeekDays();
        const expand = 'orderLine.order.dealer, status, orderLine.part, notes.createdBy, optionDetails.orderLine.part';

        const dayJobsArrays: any[][] = await Promise.all(
            this.weekDays.map((day) => this.uow.getOrderLineDetailsByDate(day, expand, true)),
        );

        const allWeekJobs: any[] = [];
        dayJobsArrays.forEach((dayJobs: any[]) => {
            dayJobs.forEach((job: any) => {
                job.shipToCityDisplay = this.getShipToCityDisplay(job);
                job.assemblyNotes = job.notes.filter((note: any) => note.assemblyNote && !note.deleted);
                job.buildTime =
                    (job.orderLine?.part?.buildTime || 0) +
                    (job.optionDetails || []).reduce((acc: number, child: any) => acc + (child.orderLine?.part?.assemblyTime || 0), 0);
                job.preAssemblyNotes = _.orderBy(
                    job.notes.filter((note: any) => note.preAssemblyNote && !note.deleted),
                    ['createDate'],
                    ['desc'],
                );
            });
            allWeekJobs.push(...dayJobs);
        });

        await this.entitiesService.setShipAddressNotes(allWeekJobs, this.shipAddresses);

        this.weekDayColumns = this.weekDays.map((date, dayIndex) => ({
            date,
            dayLabel: moment(date).format('ddd M/D'),
            jigs: this.jigColumns.map((jig) => ({
                id: `${dayIndex}-${jig.id}`,
                jigId: jig.id,
                jigName: jig.name,
                date,
                assignedJobs: _.orderBy(
                    dayJobsArrays[dayIndex].filter((job: any) => job.assemblyJigId === jig.id),
                    (x: any) => x.scheduleDisplayOrder,
                ),
            })),
        }));
    }

    async onWeekTaskDrop(e) {
        this.appInfo.pleaseWaitShow();
        e.fromData.assignedJobs.splice(e.fromIndex, 1);
        e.toData.assignedJobs.splice(e.toIndex, 0, e.itemData);

        if (e.toData.id === -1) {
            e.itemData.assemblyJigId = null;
            e.itemData.scheduled = null;
        } else {
            e.itemData.assemblyJigId = e.toData.jigId;
            e.itemData.scheduled = e.toData.date;
            e.itemData.onHold = null;
        }
        this.updateDisplayOrder(e.toData.assignedJobs, 'scheduleDisplayOrder');
        this.sortArray(e.toData.assignedJobs, 'scheduleDisplayOrder');
        await this.uow.commit();
        await this.getWeekData();
        this.appInfo.pleaseWaitHide();
    }

    async toggleHideCompleted() {
        await this.getData();
    }

    changeDate(offset: number) {
        const unit = this.viewMode === 'week' ? 'weeks' : 'days';
        this.scheduleDate = moment(this.scheduleDate).add(offset, unit as any).toDate();
    }

    async onDateChanged() {
        this.showLockHistory = false;
        await this.refresh();
    }

    onListDragStart(e) {
        e.itemData = e.fromData[e.fromIndex];
    }

    onListReorder(e) {
        this.jigColumns.splice(e.fromIndex, 1);
        this.jigColumns.splice(e.toIndex, 0, e.itemData);
        this.sessionInfo.setItem(
            this.jigOrderKey,
            this.jigColumns.map((column) => column.id),
            true,
        );
    }
    sortArray(rows: any[], fieldName: string = 'displayOrder') {
        rows.sort((a, b) => (a[fieldName] || 9999) - (b[fieldName] || 9999));
    }

    updateDisplayOrder(rows: any[], fieldName: string = 'displayOrder') {
        rows.forEach((row, index) => {
            if (row[fieldName] !== index + 1) row[fieldName] = index + 1;
        });
    }

    onTaskDragStart(e) {
        e.itemData = e.fromData.assignedJobs[e.fromIndex];
    }

    async onTaskDrop(e) {
        this.appInfo.pleaseWaitShow();
        e.fromData.assignedJobs.splice(e.fromIndex, 1);
        e.toData.assignedJobs.splice(e.toIndex, 0, e.itemData);

        if (e.toData.id === -1) {
            e.itemData.assemblyJigId = null;
            e.itemData.scheduled = null;
        } else {
            e.itemData.assemblyJigId = e.toData.id;
            e.itemData.scheduled = this.scheduleDate;
            e.itemData.onHold = null;
        }
        this.updateDisplayOrder(e.toData.assignedJobs, 'scheduleDisplayOrder');
        this.sortArray(e.toData.assignedJobs, 'scheduleDisplayOrder');
        await this.uow.commit();
        // moving on/off a jig changes the day's count, so the projection has to be rebuilt
        if (e.fromData.id < 0 !== e.toData.id < 0) await this.getData();
        else await this.getJigTimes();
        this.appInfo.pleaseWaitHide();
    }

    onContextMenu(id) {
        this.contextMenuJobId = id;
    }

    // only offer the hold action that applies to the clicked job
    onUnassignedContextMenuShowing(e: any) {
        const job = [...this.unAssignedJobs.assignedJobs, ...this.projectedJobs.assignedJobs].find((x) => x.id === this.contextMenuJobId);
        const items = this.unassignedJobContextMenuItems.map((item) => ({
            ...item,
            visible: item.index === 7 ? !job?.onHold : item.index === 8 ? !!job?.onHold : true,
        }));
        e.component.option('dataSource', items);
    }

    async onContextMenuItemClick(e: any) {
        const detail = (await this.uow.orderLineDetails.byId(this.contextMenuJobId, 'notes')) as OrderLineDetail;
        switch (e.itemData.index) {
            case 1:
                await this.openDetailDialog([detail]);
                break;
            case 2:
                await this.addNote(detail);
                break;
            case 3:
                detail.assemblyJigId = null;
                await this.uow.commit();
                break;
            case 4:
                await this.rescheduleJob(detail);
                break;
            case 5:
                await this.completeJob(detail);
                break;
            case 6:
                await this.unCompleteJob(detail);
                break;
            case 7:
                await this.putOnHold(detail);
                return;
            case 8:
                detail.onHold = null;
                await this.uow.commit();
                break;
        }
        await this.getData();
    }

    async addNote(detail: any) {
        const isAssigned = this.assignedJobs.some((job) => job.id === detail.id);
        const isUnassigned = [...this.unAssignedJobs.assignedJobs, ...this.projectedJobs.assignedJobs].some((job) => job.id === detail.id);

        // if (!isAssigned && !isUnassigned) {
        //     alert('Job not found in assigned or unassigned jobs.', this.appInfo.settings.company);
        //     return;
        // }

        let lastNote: OrderLineDetailNote;
        // if (isAssigned) {
        //     lastNote = _.orderBy(detail.notes, ['created'], ['desc']).find((note) => note.assemblyNote);
        // } else {
        //     lastNote = _.orderBy(detail.notes, ['created'], ['desc']).find((note) => note.preAssemblyNote);
        // }

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Jig Note',
                dialogPrompt: `Enter note`,
                dialogValue: lastNote ? lastNote.note : '',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && result.value) {
                if (lastNote) lastNote.note = result.value;
                else {
                    const newNote = this.uow.orderLineDetailNotes.createEntity({
                        note: result.value,
                        assemblyNote: isAssigned,
                        preAssemblyNote: isUnassigned,
                        orderLineDetailId: detail.id,
                        createDate: new Date(),
                        createdById: this.auth.currentUser.id,
                    });
                }
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async putOnHold(detail: OrderLineDetail) {
        const holdNotes = (await this.uow.holdNotes.whereActive('displayOrder')) as any[];
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Put On Hold',
                dialogPrompt: 'Select a reason',
                dialogValueArray: holdNotes.map((holdNote) => holdNote.name),
                mode: 'selectWithOther',
                selectionRequired: true,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && result.value) {
                this.appInfo.pleaseWaitShow();
                detail.onHold = new Date();
                this.uow.orderLineDetailNotes.createEntity({
                    note: `On Hold: ${result.value}`,
                    preAssemblyNote: true,
                    orderLineDetailId: detail.id,
                    createDate: new Date(),
                    createdById: this.auth.currentUser.id,
                });
                await this.uow.commit();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async editNote(note: any) {
        console.log(note);
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Jig Note',
                dialogPrompt: `Enter note`,
                dialogValue: note.note,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result) {
                this.appInfo.pleaseWaitShow();
                note.note = result.value;
                await this.uow.commit();
                await this.getAssemblyJigs();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async deleteNote(note: any) {
        const result = await confirm('Are you sure you want to delete this note?', 'Confirm Delete');
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        note.deleted = new Date();
        note.deletedBy = this.auth.currentUser.userName;
        await this.uow.commit();
        await this.getAssemblyJigs();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async rescheduleJob(detail: any) {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Adjust Date',
                dialogPrompt: 'Enter New Date',
                dialogValue: detail.scheduled,
                mode: 'date',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                this.appInfo.pleaseWaitShow();
                detail.scheduled = result.value;
                await this.uow.commit();
                await this.getAssemblyJigs();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async completeJob(detail: any) {
        this.appInfo.pleaseWaitShow();
        detail.buildComplete = new Date();
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
    }

    async unCompleteJob(detail: any) {
        this.appInfo.pleaseWaitShow();
        detail.buildComplete = null;
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
    }

    onJigContextMenu(id) {
        this.contextMenuJigId = id;
    }

    async onJigContextMenuItemClick(e: any) {
        // console.log(e);
        // console.log(this.contextMenuJigId);
        switch (e.itemData.index) {
            case 1:
                await this.renameJig(this.contextMenuJigId);
                break;
            case 2:
                await this.editJigMembers(this.contextMenuJigId);
                break;
            case 3:
                await this.addJigNote(this.contextMenuJigId);
                // detail.assemblyJigId = null;
                // await this.uow.commit();
                break;
            case 4:
                await this.rescheduleJig(this.contextMenuJigId);
                break;
            case 5:
                this.print(this.contextMenuJigId);
                break;
            case 6:
                // await this.unCompleteJob(detail);
                break;
        }
        await this.getData();
    }

    async editJigMembers(jigId: number) {
        const jig = (await this.uow.assemblyJigs.byId(jigId)) as AssemblyJig;
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Edit Team Members',
                dialogPrompt: `Enter team members`,
                dialogValueArray: this.teamMembers,
                mode: 'tagBox',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && result.value) {
                result.value.forEach((value) => {
                    const teamMember = this.teamMembers.find((member) => member.id === value);
                    const newMember = this.uow.assemblyJigMembers.createEntity({
                        scheduled: this.scheduleDate,
                        assemblyJigId: jigId,
                        userName: teamMember.name,
                    });
                });
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getAssemblyJigs();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }
    async deleteJigMember(member: any) {
        this.appInfo.pleaseWaitShow();
        member.deleted = new Date();
        await this.uow.commit();
        await this.getAssemblyJigs();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async addJigNote(jigId: number) {
        const jig = (await this.uow.assemblyJigs.byId(jigId)) as AssemblyJig;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Jig Note',
                dialogPrompt: `Enter note`,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && result.value) {
                this.uow.assemblyJigNotes.createEntity({
                    scheduled: this.scheduleDate,
                    assemblyJigId: jigId,
                    notes: result.value,
                });
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getAssemblyJigs();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }
    async editJigNote(note: AssemblyJigNote) {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Jig Note',
                dialogPrompt: `Enter note`,
                dialogValue: note.notes,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result) {
                this.appInfo.pleaseWaitShow();
                note.notes = result.value;
                await this.uow.commit();
                await this.getAssemblyJigs();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async renameJig(jigId: number) {
        const jig = (await this.uow.assemblyJigs.byId(jigId)) as AssemblyJig;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Rename Jig',
                dialogPrompt: `Enter the new name for ${jig.name}`,
                dialogValue: jig.name,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && result.value) {
                jig.name = result.value;
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getAssemblyJigs();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async rescheduleJig(jigId: number) {
        const rescheduleDialog = custom({
            title: `${this.appInfo.settings.company}`,
            messageHtml: `Do you want to ReSchedule ALL details or UNBUILT details?`,
            buttons: [
                {
                    text: 'All',
                    onClick: (e) => {
                        return 'all';
                    },
                },
                {
                    text: 'UnBuilt Only',
                    onClick: (e) => {
                        return 'unbuilt';
                    },
                },
                {
                    text: 'Cancel',
                    onClick: (e) => {
                        return 'cancel';
                    },
                },
            ],
        });
        const rescheduleResult = await rescheduleDialog.show();
        if (rescheduleResult === 'cancel') return;

        const newDate = await this.getNewDate();
        if (!newDate) return;

        this.appInfo.pleaseWaitShow();
        const jigDetails = this.assignedJobs.filter((job) => job.assemblyJigId === jigId && (rescheduleResult === 'unbuilt' ? !job.built : true));
        jigDetails.forEach((detail) => {
            detail.scheduled = newDate;
        });
        await this.uow.commit();
        await this.refresh();
        this.appInfo.pleaseWaitHide();
    }

    async openOptions(orderLineDetail: OrderLineDetail) {
        if (!orderLineDetail) return;

        await this.appInfo.pleaseWaitShow();
        await this.uow.getOptionsForOrderLineDetail(orderLineDetail.id);
        this.entitiesService.setOptionHtml([orderLineDetail]);
        await this.appInfo.pleaseWaitHide();

        const modalRef = this.modalService.show(OptionsDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                html: !!orderLineDetail.optionHTML ? orderLineDetail.optionHTML : '(no options)',
            },
        });
        modalRef.content.result.subscribe(async (_) => {
            modalRef.hide();
        });
    }

    async openDetailDialog(details: OrderLineDetail[]) {
        const modalRef = this.modalService.show(OrderBuildDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                buildScheduleIds: details.map((d) => d.id),
                showAsDialog: true,
                storageKey: 'build-scheduler-orderlinedetail-grid',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            await this.refresh();
            modalRef.hide();
        });
    }

    print(jigId?: number, params?: { printWeek?: boolean }) {
        let jigIds = [];
        if (!jigId) jigIds = this.assemblyJigs.map((jig) => jig.id);
        else jigIds = [jigId];

        this.reportService.printAssemblyJigSchedules(this.scheduleDate, params?.printWeek, jigIds);
    }

    async getNewDate(): Promise<Date> {
        let endDate = new Date();
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Set Date',
                dialogPrompt: `Enter the new date`,
                dialogValue: endDate,
                mode: 'date',
            },
        });

        const promise = new Promise<Date>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.result && result.value) {
                    endDate = moment(result.value).startOf('day').toDate();
                } else {
                    endDate = null;
                }
                modalRef.hide();
                resolve(endDate);
            });
        });
        return promise;
    }
}
